/**
 * Shared visual system for the onboarding screens — the flow's type scale is
 * deliberately larger than the app's (one idea per screen), and green is
 * budgeted: selected states, eyebrows, CTAs and exactly one full-green screen
 * (the reveal). Flat rules hold: no gradients, no shadows.
 */

export const GREEN = "#22c55e";
export const GREEN_PRESSED = "#16a34a";
/** Light green for SMALL text on dark backgrounds (AA contrast). */
export const GREEN_TEXT_DARK = "#4ade80";
/** Dark green for small text on light backgrounds. */
export const GREEN_TEXT_LIGHT = "#16a34a";

export const textPrimary = (isDark: boolean) => (isDark ? "#ffffff" : "#1d1d1f");
export const textSecondary = (isDark: boolean) => (isDark ? "#9ca3af" : "#64748b");
/** Body copy on the aurora — near-white; the grey read as unimportant. */
export const textBody = (isDark: boolean) =>
  isDark ? "rgba(255, 255, 255, 0.92)" : "#374151";
export const hairline = (isDark: boolean) =>
  isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
export const restingSurface = (isDark: boolean) =>
  isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)";
export const greenText = (isDark: boolean) =>
  isDark ? GREEN_TEXT_DARK : GREEN_TEXT_LIGHT;

/** Hook / commitment / reveal headline. */
export const displayTitleSx = (isDark: boolean) => ({
  fontWeight: 800,
  fontSize: "2.25rem",
  lineHeight: 1.1,
  letterSpacing: "-0.03em",
  color: textPrimary(isDark),
});

/** Question and interstitial titles. */
export const stepTitleSx = (isDark: boolean) => ({
  fontWeight: 750,
  fontSize: "1.75rem",
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
  color: textPrimary(isDark),
});

export const stepSubtitleSx = (isDark: boolean) => ({
  mt: 1.25,
  fontSize: "1rem",
  lineHeight: 1.5,
  color: textBody(isDark),
});

/** Green micro-caps eyebrow label. */
export const eyebrowSx = (isDark: boolean) => ({
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color: greenText(isDark),
});

/** 56px primary CTA. Pressed state is scale via framer-motion at call sites;
    the :active fallback covers plain Buttons. */
export const primaryButtonSx = {
  backgroundColor: GREEN,
  color: "#ffffff",
  fontWeight: 700,
  textTransform: "none",
  height: 56,
  fontSize: "1.0625rem",
  borderRadius: 2.5,
  "&:hover": { backgroundColor: GREEN_PRESSED },
  "&:active": { transform: "scale(0.97)" },
  "&:disabled": { backgroundColor: "rgba(34, 197, 94, 0.35)", color: "#ffffff" },
} as const;

/** Pinned bottom CTA zone — every step's primary action lives here, never
    floating mid-content. */
export const ctaZoneSx = { mt: 3 } as const;
